import React, { useState, useEffect } from "react";
import { 
  ShoppingBag, CheckCircle, Clock, Truck, 
  PackageCheck, QrCode, ShoppingCart, User, 
  Phone, MapPin, KeyRound, Loader2, Search, ArrowUpDown, Filter, Megaphone, Calendar
} from "lucide-react";
import api from "../lib/api"; 
import { formatVND } from "../lib/utils";
import { ProductCardsSkeleton } from "../components/ui/Skeleton";

const stripAccents = (s) => { if (!s) return ''; return String(s).toLowerCase().replace(/đ/g, 'd').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); };

export default function CustomerOrder() {
  // --- Các State quản lý sản phẩm và giỏ hàng ---
  const [products, setProducts] = useState([]); 
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [quantities, setQuantities] = useState({}); 

  // --- State quản lý cấu hình động từ Admin ---
  const [shopSettings, setShopSettings] = useState({
    logo_url: "",
    introduction: "",
    order_page_content: ""
  });

  // --- State phục vụ Tìm kiếm, Lọc, Sắp xếp ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("none"); 

  // --- Các State quản lý thông tin Form khách hàng ---
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState(""); 
  const [verifyCode, setVerifyCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash"); 
  
  // --- THÊM MỚI: State quản lý Ngày & Ca giao hàng ---
  const todayStr = new Date().toLocaleDateString("sv-SE"); // Chuẩn YYYY-MM-DD
  const [deliveryDate, setDeliveryDate] = useState(todayStr);
  const [deliveryShift, setDeliveryShift] = useState("");

  // --- Các State quản lý trạng thái đơn hàng ---
  const [viewMode, setViewMode] = useState("order"); 
  const [currentOrder, setCurrentOrder] = useState(null); 
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // --- Các State phục vụ tính năng lọc kiểm tra hội viên ---
  const [checkingCustomer, setCheckingCustomer] = useState(false);
  const [isRegisteredCustomer, setIsRegisteredCustomer] = useState(null); 
  const [matchedCustomerData, setMatchedCustomerData] = useState(null); 

  // =========================================================
  // 1. TẢI SẢN PHẨM & CẤU HÌNH SHOP TỪ ADMIN SERVER
  // =========================================================
  const loadInitialData = async () => {
    try {
      const [prodRes, settingsRes] = await Promise.all([
        api.get("/products"),
        api.get("/settings").catch(() => ({ data: null }))
      ]);

      if (Array.isArray(prodRes.data)) {
        const activeItems = prodRes.data.filter(p => p.is_active !== false);
        setProducts(activeItems);
      }

      if (settingsRes && settingsRes.data) {
        setShopSettings({
          logo_url: settingsRes.data.logo_url || "",
          introduction: settingsRes.data.introduction || "",
          order_page_content: settingsRes.data.order_page_content || ""
        });
      }
    } catch (err) {
      console.error("Lỗi đồng bộ dữ liệu hệ thống:", err);
      setError("Không thể kết nối thực đơn của tiệm. Vui lòng tải lại trang!");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // THEO DÕI TIẾN ĐỘ ĐƠN HÀNG REALTIME
  useEffect(() => {
    let intervalId;
    const orderId = currentOrder?.order_id || currentOrder?.id;

    if (viewMode === "tracking" && orderId) {
      intervalId = setInterval(async () => {
        try {
          const { data } = await api.get(`/orders/${orderId}`);
          if (data) {
            setCurrentOrder(prev => ({ ...data, fallback_total: prev?.fallback_total }));
          }
        } catch (err) {
          console.log("Đang kết nối cập nhật tiến độ đơn hàng...");
        }
      }, 4000); 
    }
    return () => clearInterval(intervalId);
  }, [viewMode, currentOrder]);

  // =========================================================
  // 2. CHECK HỘI VIÊN & ĐIỀN THÔNG TIN TỰ ĐỘNG
  // =========================================================
  const checkCustomerPhone = async (phoneValue) => {
    const cleanedPhone = phoneValue.trim();
    if (!cleanedPhone) {
      setIsRegisteredCustomer(null);
      setMatchedCustomerData(null);
      return;
    }

    setCheckingCustomer(true);
    setError("");

    try {
      const { data } = await api.get("/customers", { params: { q: cleanedPhone } });
      if (Array.isArray(data) && data.length > 0) {
        const matchedCustomer = data.find(
          (c) => c.phone?.trim() === cleanedPhone || c.phone?.trim().replace(/[\s.-]/g, "") === cleanedPhone
        );

        if (matchedCustomer) {
          setIsRegisteredCustomer(true);
          setMatchedCustomerData(matchedCustomer); 
          if (matchedCustomer.name) setCustomerName(matchedCustomer.name);
          if (matchedCustomer.phone) setPhone(matchedCustomer.phone.trim());
          if (matchedCustomer.address) setAddress(matchedCustomer.address); 
        } else {
          setIsRegisteredCustomer(false);
          setMatchedCustomerData(null);
          setError("Bạn chưa là khách hàng của hệ thống tiệm bánh!");
        }
      } else {
        setIsRegisteredCustomer(false);
        setMatchedCustomerData(null);
        setError("Bạn chưa là khách hàng của hệ thống tiệm bánh!");
      }
    } catch (err) {
      console.error(err);
      setIsRegisteredCustomer(true); 
    } finally {
      setCheckingCustomer(false);
    }
  };

  const handlePhoneBlur = () => {
    checkCustomerPhone(phone);
  };

  const isCodeVerified = phone.trim().length >= 5 && verifyCode.trim() === phone.trim().slice(-5);

  // =========================================================
  // 3. ĐIỀU CHỈNH SỐ LƯỢNG MÓN ĂN
  // =========================================================
  const handleQuantityChange = (productId, val) => {
    const currentQty = parseInt(quantities[productId], 10) || 0;
    const newQty = Math.max(0, currentQty + val);
    if (newQty === 0) {
      const updated = { ...quantities };
      delete updated[productId];
      setQuantities(updated);
    } else {
      setQuantities({ ...quantities, [productId]: newQty });
    }
  };

  const handleDirectQuantityChange = (productId, textValue) => {
    if (textValue === "") {
      setQuantities({ ...quantities, [productId]: "" }); 
      return;
    }
    const parsed = parseInt(textValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      if (parsed === 0) {
        const updated = { ...quantities };
        delete updated[productId];
        setQuantities(updated);
      } else {
        setQuantities({ ...quantities, [productId]: parsed });
      }
    }
  };

  const uniqueCategories = ["all", ...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredAndSortedProducts = products
    .filter((product) => {
      const matchSearch = stripAccents(product.name).includes(stripAccents(searchTerm)) ||
                          (product.category && stripAccents(product.category).includes(stripAccents(searchTerm)));
      const matchCategory = selectedCategory === "all" || product.category === selectedCategory;
      return matchSearch && matchCategory;
    })
    .sort((a, b) => {
      if (sortBy === "price-asc") return a.price - b.price;
      if (sortBy === "price-desc") return b.price - a.price;
      if (sortBy === "alpha-az") return a.name.localeCompare(b.name, "vi");
      if (sortBy === "alpha-za") return b.name.localeCompare(a.name, "vi");
      return 0; 
    });

  const selectedItems = Object.keys(quantities)
    .filter((id) => parseInt(quantities[id], 10) > 0)
    .map((id) => {
      const prod = products.find((p) => String(p.product_id) === String(id));
      return {
        product_id: id,
        name: prod?.name || "Sản phẩm",
        image_url: prod?.image_url || "",
        price: prod?.price || 0,
        quantity: parseInt(quantities[id], 10),
      };
    });

  const totalQuantity = selectedItems.reduce((acc, item) => acc + item.quantity, 0);
  const totalAmount = selectedItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  // =========================================================
  // 4. XỬ LÝ ĐẶT HÀNG
  // =========================================================
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setError("");

    if (selectedItems.length === 0) {
      setError("Giỏ hàng của bạn đang trống. Vui lòng chọn sản phẩm trước!");
      return;
    }
    if (isRegisteredCustomer === false) {
      setError("Không thể đặt hàng! Bạn chưa là khách hàng của hệ thống tiệm bánh.");
      return;
    }
    if (!isCodeVerified) {
      setError("Mã xác nhận chưa chính xác!");
      return;
    }

    // --- KIỂM TRA RÀNG BUỘC CHỌN CA ---
    if (deliveryDate > todayStr && !deliveryShift) {
      setError("Vui lòng chọn ca giao hàng cho ngày đặt trước!");
      return;
    }

    setSubmitting(true);
    const finalCustomerPhone = matchedCustomerData?.phone ? matchedCustomerData.phone.trim() : phone.trim();
    const finalCustomerName = customerName.trim() || matchedCustomerData?.name || "Khách hàng hội viên";

    // --- CẬP NHẬT PAYLOAD THÊM NGÀY & CA ---
    const orderPayload = {
      customer_name: finalCustomerName,
      phone: finalCustomerPhone,         
      customer_phone: finalCustomerPhone, 
      address: address.trim(),           
      payment_method: paymentMethod,
      total_amount: totalAmount, 
      delivery_date: deliveryDate,           // Thêm mới
      delivery_shift: deliveryShift || null, // Thêm mới
      items: selectedItems.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        name: item.name,          
        price: item.price,        
        unit_price: item.price,   
        subtotal: item.price * item.quantity, 
      })),
    };

    try {
      const finalTotal = totalAmount; 
      const { data } = await api.post("/orders", orderPayload);
      
      setCurrentOrder({ ...data, fallback_total: finalTotal });
      setViewMode("tracking"); 
      
      // Reset State
      setQuantities({});
      setCustomerName("");
      setPhone("");
      setAddress("");
      setVerifyCode("");
      setDeliveryDate(todayStr); // Reset
      setDeliveryShift("");      // Reset
      setIsRegisteredCustomer(null);
      setMatchedCustomerData(null);
    } catch (err) {
      console.error(err);
      setError("Gửi đơn hàng thất bại sang bên tiệm. Vui lòng kiểm tra lại kết nối!");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-ink antialiased pb-12">
      {/* HEADER: HIỂN THỊ LOGO ĐỘNG CỦA ADMIN NẾU CÓ */}
      <header className="bg-bamboo text-white sticky top-0 z-50 shadow-md">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setViewMode("order")}>
            {shopSettings.logo_url ? (
              <img 
                src={shopSettings.logo_url} 
                alt="Shop Logo" 
                className="h-10 w-10 object-cover rounded-full border-2 border-white/40 shadow-sm"
              />
            ) : (
              <ShoppingBag size={26} className="text-white" />
            )}
            <span className="text-xl font-heading font-black tracking-tight">Bánh Bao Cần Gia</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs font-medium">
            <ShoppingCart size={16} />
            <span>Giỏ hàng ({totalQuantity})</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 mt-6">
        {viewMode === "order" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            
            <div className="lg:col-span-2 space-y-4">
              
              {/* VÙNG 1: HIỂN THỊ THÔNG TIN GIỚI THIỆU & THÔNG BÁO TỪ ADMIN */}
              {(shopSettings.introduction || shopSettings.order_page_content) && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg shadow-sm border border-amber-200/60 space-y-2 animate-in fade-in duration-300">
                  {shopSettings.introduction && (
                    <div className="text-xs text-amber-900 leading-relaxed font-medium">
                      ℹ️ <span className="font-bold">Giới thiệu tiệm:</span> {shopSettings.introduction}
                    </div>
                  )}
                  {shopSettings.order_page_content && (
                    <div className="flex items-start gap-2 bg-white/70 p-2.5 rounded border border-orange-200 text-xs text-orange-900">
                      <Megaphone size={16} className="text-orange-600 shrink-0 mt-0.5" />
                      <div><span className="font-bold">Lưu ý đơn hàng:</span> {shopSettings.order_page_content}</div>
                    </div>
                  )}
                </div>
              )}

              {/* THANH ĐIỀU KHIỂN: TÌM KIẾM, LỌC, SẮP XẾP */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm tên bánh..."
                    className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-bamboo bg-gray-50/50"
                  />
                </div>
                <div className="relative">
                  <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-bamboo bg-white capitalize"
                  >
                    <option value="all">📁 Tất cả danh mục</option>
                    {uniqueCategories.filter(cat => cat !== "all").map((cat, idx) => (
                      <option key={idx} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <ArrowUpDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:border-bamboo bg-white"
                  >
                    <option value="none">🔄 Thức tự mặc định</option>
                    <option value="price-asc">💰 Giá: Thấp đến Cao</option>
                    <option value="price-desc">💰 Giá: Cao đến Thấp</option>
                    <option value="alpha-az">🔤 Tên: A đến Z</option>
                    <option value="alpha-za">🔤 Tên: Z đến A</option>
                  </select>
                </div>
              </div>

              {/* DANH SÁCH BÁNH */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4 border-b pb-2">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">Thực đơn tại quầy</h2>
                  <span className="text-xs text-gray-400">Tìm thấy ({filteredAndSortedProducts.length})</span>
                </div>
                
                {loadingProducts ? (
                  <ProductCardsSkeleton count={6} />
                ) : filteredAndSortedProducts.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400 italic">Không tìm thấy sản phẩm nào!</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {filteredAndSortedProducts.map((product) => {
                      const qty = quantities[product.product_id] || "";
                      const hasQty = parseInt(qty, 10) > 0;

                      return (
                        <div key={product.product_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all flex flex-col group">
                          <div className="relative pt-[100%] overflow-hidden bg-gray-100">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs italic">Không có ảnh</div>
                            )}
                          </div>
                          
                          <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
                            <div>
                              <h3 className="text-sm font-medium text-gray-800 line-clamp-2 min-h-[40px] leading-snug">{product.name}</h3>
                              <div className="text-xs text-gray-400 mt-0.5">{product.category}</div>
                            </div>
                            
                            <div className="pt-2 flex flex-col space-y-2">
                              <div className="font-mono text-bamboo font-bold text-sm text-center">{formatVND(product.price)}</div>
                              
                              {!hasQty && qty !== "" ? (
                                <button
                                  type="button"
                                  onClick={() => handleQuantityChange(product.product_id, 1)}
                                  className="w-full text-center bg-bamboo/10 hover:bg-bamboo hover:text-white text-bamboo text-xs font-bold py-2 rounded border border-bamboo/20 transition-colors"
                                >
                                  + Chọn mua
                                </button>
                              ) : (
                                <div className="flex items-center justify-between border border-bamboo rounded overflow-hidden bg-white shadow-inner">
                                  <button type="button" onClick={() => handleQuantityChange(product.product_id, -1)} className="px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-xs border-r">-</button>
                                  <input
                                    type="number"
                                    min="0"
                                    value={qty}
                                    onChange={(e) => handleDirectQuantityChange(product.product_id, e.target.value)}
                                    onBlur={(e) => {
                                      if (e.target.value === "" || parseInt(e.target.value, 10) === 0) {
                                        const updated = { ...quantities };
                                        delete updated[product.product_id];
                                        setQuantities(updated);
                                      }
                                    }}
                                    placeholder="0"
                                    className="w-full text-center font-bold text-xs text-bamboo focus:outline-none bg-white p-1 font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                  <button type="button" onClick={() => handleQuantityChange(product.product_id, 1)} className="px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-xs border-l">+</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* CỘT PHẢI: CHI TIẾT GIỎ HÀNG VÀ FORM THÔNG TIN ĐẶT BÁNH */}
            <div className="space-y-4">
              <form onSubmit={handleSubmitOrder} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-sm text-gray-700 flex items-center gap-2">
                  <ShoppingCart size={16} className="text-bamboo" /> Chi tiết giỏ hàng
                </div>
                
                <div className="p-4 space-y-4">
                  {selectedItems.length === 0 ? (
                    <div className="text-center py-6 text-xs text-gray-400 italic">Chưa chọn món ăn nào.</div>
                  ) : (
                    <div className="divide-y divide-gray-100 max-h-44 overflow-y-auto pr-1">
                      {selectedItems.map((item) => (
                        <div key={item.product_id} className="flex justify-between items-center py-2 text-xs">
                          <span className="font-medium text-gray-700 truncate max-w-[160px]">{item.name}</span>
                          <span className="text-gray-400 font-mono">x{item.quantity}</span>
                          <span className="font-mono text-gray-600">{formatVND(item.price * item.quantity)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2 font-bold text-sm text-bamboo">
                        <span>Tổng tiền đơn:</span>
                        <span>{formatVND(totalAmount)}</span>
                      </div>
                    </div>
                  )}

                  {error && <div className="p-2.5 bg-red-50 text-red-600 rounded text-xs font-semibold border border-red-200 mb-2">{error}</div>}

                  <div className="space-y-3 pt-3 border-t border-gray-100 text-xs">
                    <div>
                      <label className="font-bold text-gray-600 mb-1 block flex items-center gap-1">
                        <Phone size={13} className="text-gray-400" /> Số điện thoại đặt bánh *
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          required
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          onBlur={handlePhoneBlur} 
                          className={`w-full px-3 py-2 border rounded focus:outline-none font-mono ${
                            isRegisteredCustomer === false ? "border-red-400 bg-red-50" : isRegisteredCustomer === true ? "border-emerald-400 bg-emerald-50/30" : "border-gray-300 focus:border-bamboo"
                          }`}
                          placeholder="Nhập SĐT nhận bánh"
                        />
                        {checkingCustomer && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] text-gray-400">
                            <Loader2 size={12} className="animate-spin text-bamboo" /> Đang tra cứu...
                          </div>
                        )}
                      </div>
                      {isRegisteredCustomer === false && <p className="text-red-500 text-[11px] mt-1 font-semibold">⚠️ Bạn chưa là khách hàng hệ thống.</p>}
                    </div>

                    <div>
                      <label className="font-bold text-gray-600 mb-1 block flex items-center gap-1">
                        <KeyRound size={13} className="text-gray-400" /> Mã xác nhận *
                      </label>
                      <input
                        type="text"
                        maxLength={5}
                        required
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded font-mono tracking-widest focus:outline-none focus:border-bamboo bg-gray-50"
                        placeholder="Nhập 5 số đuôi của SĐT để hiện địa chỉ"
                      />
                    </div>

                    {isCodeVerified && (
                      <div className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        {/* Tên & Địa chỉ */}
                        <div className="space-y-3">
                          <div>
                            <label className="font-bold text-emerald-800 mb-1 block flex items-center gap-1">
                              <User size={13} className="text-emerald-600" /> Tên khách hàng *
                            </label>
                            <input
                              type="text"
                              required
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              className="w-full px-3 py-2 border border-emerald-200 rounded focus:outline-none focus:border-emerald-500 bg-white"
                              placeholder="Họ và tên khách hàng"
                            />
                          </div>

                          <div>
                            <label className="font-bold text-emerald-800 mb-1 block flex items-center gap-1">
                              <MapPin size={13} className="text-emerald-600" /> Địa chỉ giao nhận bánh *
                            </label>
                            <input
                              type="text"
                              required
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              className="w-full px-3 py-2 border border-emerald-200 rounded focus:outline-none focus:border-emerald-500 bg-white"
                              placeholder="Số nhà, tên đường..."
                            />
                          </div>
                        </div>

                        {/* --- KHU VỰC THÊM MỚI: NGÀY & CA GIAO HÀNG --- */}
                        <div className="pt-3 border-t border-emerald-100/60 space-y-3">
                          <div>
                            <label className="font-bold text-emerald-800 mb-1 block flex items-center gap-1">
                              <Calendar size={13} className="text-emerald-600" /> Ngày muốn nhận bánh *
                            </label>
                            <input
                              type="date"
                              min={todayStr}
                              value={deliveryDate}
                              onChange={(e) => {
                                setDeliveryDate(e.target.value);
                                // Nếu chọn lại hôm nay thì clear Ca
                                if (e.target.value === todayStr) {
                                  setDeliveryShift("");
                                }
                              }}
                              className="w-full px-3 py-2 border border-emerald-200 rounded focus:outline-none focus:border-emerald-500 bg-white"
                            />
                          </div>

                          {/* Chỉ hiện chọn ca nếu chọn giao trong ngày tương lai */}
                          {deliveryDate > todayStr && (
                            <div className="animate-in fade-in duration-300">
                              <label className="font-bold text-emerald-800 mb-1 block flex items-center gap-1">
                                <Clock size={13} className="text-emerald-600" /> Chọn ca giao hàng *
                              </label>
                              <select
                                required
                                value={deliveryShift}
                                onChange={(e) => setDeliveryShift(e.target.value)}
                                className="w-full px-3 py-2 border border-emerald-200 rounded focus:outline-none focus:border-emerald-500 bg-white"
                              >
                                <option value="" disabled>— Vui lòng chọn ca giao —</option>
                                <option value="ca1">Ca 1 (Sáng: 8h00 - 12h30)</option>
                                <option value="ca2">Ca 2 (Chiều: 12h30 - 18h00)</option>
                              </select>
                            </div>
                          )}
                        </div>
                        {/* ------------------------------------------- */}
                      </div>
                    )}

                    <div className="pt-2">
                      <label className="font-bold text-gray-600 mb-1 block">Phương thức thanh toán</label>
                      <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-bamboo bg-white">
                        <option value="cash">💵 Tiền mặt khi nhận bánh</option>
                        <option value="transfer">🏦 Chuyển khoản ngân hàng</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || selectedItems.length === 0 || isRegisteredCustomer === false || checkingCustomer || !isCodeVerified}
                    className="w-full bg-bamboo hover:bg-bamboo/90 text-white font-bold py-2.5 rounded text-sm shadow uppercase tracking-wide mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                  >
                    {submitting && <Loader2 className="animate-spin" size={16} />}
                    {submitting ? "Đang gửi đơn sang tiệm..." : "Đặt mua hàng ngay"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          
          /* MÀN HÌNH TRACKING TRẠNG THÁI ĐƠN HÀNG */
          <div className="max-w-xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden space-y-6 p-6">
            <div className="text-center border-b border-gray-100 pb-4">
              <CheckCircle size={48} className="text-emerald-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-gray-800">Đặt hàng thành công!</h2>
              <p className="text-xs text-gray-400 mt-1">Mã đơn: <span className="font-mono font-bold text-gray-700">{currentOrder?.order_id || currentOrder?.id}</span></p>
            </div>

            <div className="grid grid-cols-4 gap-1 text-center bg-gray-50 py-3 rounded-lg border border-gray-100">
              <div className={`space-y-1 ${currentOrder?.status === "processing" ? "text-bamboo font-bold" : "text-gray-400"}`}>
                <Clock size={16} className="mx-auto" />
                <div className="text-[10px]">Đang xử lý</div>
              </div>
              <div className={`space-y-1 ${currentOrder?.status === "preparing" ? "text-bamboo font-bold" : "text-gray-400"}`}>
                <ShoppingBag size={16} className="mx-auto" />
                <div className="text-[10px]">Chuẩn bị</div>
              </div>
              <div className={`space-y-1 ${currentOrder?.status === "delivering" ? "text-bamboo font-bold" : "text-gray-400"}`}>
                <Truck size={16} className="mx-auto" />
                <div className="text-[10px]">Đang giao</div>
              </div>
              <div className={`space-y-1 ${currentOrder?.status === "delivered" ? "text-emerald-600 font-bold" : "text-gray-400"}`}>
                <PackageCheck size={16} className="mx-auto" />
                <div className="text-[10px]">Đã giao</div>
              </div>
            </div>

            <div className="text-xs space-y-2.5 bg-gray-50/50 p-4 rounded-lg border border-gray-100 text-gray-600">
              <div><strong>Khách hàng:</strong> {currentOrder?.customer_name} ({currentOrder?.phone || currentOrder?.customer_phone})</div>
              <div><strong>Địa chỉ:</strong> <span className="text-gray-900 font-medium">{currentOrder?.address || "Nhận tại cửa hàng"}</span></div>
              
              {/* --- HIỂN THỊ THÔNG TIN GIAO HÀNG TẠI TRACKING (Nếu có) --- */}
              {currentOrder?.delivery_date && (
                <div>
                  <strong>Thời gian nhận:</strong> <span className="text-emerald-600 font-medium">Ngày {currentOrder.delivery_date} {currentOrder.delivery_shift === "ca1" ? "(Sáng)" : currentOrder.delivery_shift === "ca2" ? "(Chiều)" : ""}</span>
                </div>
              )}

              <div>
                <strong>Danh sách món:</strong>
                <ul className="list-disc pl-5 mt-1 text-gray-500 space-y-1">
                  {currentOrder?.items?.map((item, idx) => (
                    <li key={idx}>{item.name} (Số lượng: {item.quantity})</li>
                  ))}
                </ul>
              </div>
              <div className="font-bold text-bamboo text-sm pt-2 border-t mt-2 flex justify-between">
                <span>Tổng tiền:</span>
                <span>{formatVND(currentOrder?.total_amount || currentOrder?.total_price || currentOrder?.fallback_total || 0)}</span>
              </div>
              
              <div className="pt-2 text-xs">
                <strong>Thanh toán:</strong> {currentOrder?.payment_method === "cash" ? <span className="text-amber-600 font-bold">Tiền mặt</span> : <span className="text-blue-600 font-bold">Chuyển khoản</span>}
              </div>

              {currentOrder?.payment_method === "transfer" && (
                <div className="mt-3 p-3 rounded bg-blue-50/70 border border-blue-200 text-xs">
                  {!currentOrder?.is_manager_confirmed ? (
                    <p className="text-blue-700 italic text-center animate-pulse">⏳ Đơn hàng đang chờ Tiệm duyệt để lấy mã QR...</p>
                  ) : (
                    <div className="text-center space-y-2">
                      <p className="text-emerald-700 font-bold">✓ Tiệm đã duyệt đơn! Mã QR quét chuyển khoản:</p>
                      <div className="w-32 h-32 bg-white mx-auto border border-blue-300 flex items-center justify-center rounded p-1 shadow-sm">
                        <QrCode size={100} className="text-blue-950" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button onClick={() => setViewMode("order")} className="w-full text-center text-xs text-bamboo hover:underline font-bold pt-2 block">← Quay lại thực đơn đặt thêm bánh</button>
          </div>
        )}
      </div>
    </div>
  );
}